import { ccfRoutes } from '@app/ccf/ccf-routes';
import { CompanyReport, CompanyReportStatusEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import Loader from '@shared/components/loader';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardFooter } from '@shared/components/ui/card';
import { TableCell } from '@shared/components/ui/table';
import { cn, formatBigNumber, formatDate } from '@shared/lib/utils';
import React from 'react';
import { generatePath, Link, useNavigate } from 'react-router-dom';

interface CCFReportCardProps extends React.HTMLAttributes<HTMLDivElement> {
  report: CompanyReport;
}

export default function CCFReportCard({
  report,
  className,
  onClick,
}: CCFReportCardProps) {
  const published =
    report.status === CompanyReportStatusEnum.published ||
    report.status === CompanyReportStatusEnum.edited ||
    report.lastPublishedAt;
  const publishing = report.status === CompanyReportStatusEnum.publishing;
  const navigate = useNavigate();

  const reportInfoContent: ReportInfoProps[] = [
    {
      label: 'Created',
      value: formatDate(report.created, 'do MMMM yyyy'),
    },

    ...(report.lastPublishedAt
      ? [
          {
            label: 'Last Published',
            value: formatDate(report.lastPublishedAt, 'do MMMM yyyy'),
          },
        ]
      : []),
  ];

  return (
    <TableCell className={cn('p-0.6', className)}>
      <Card
        className={cn(
          'min-w-96 mb-3.5 mr-0 border-primary-50 transition-all cursor-default',
          {
            'hover:border-primary-200 hover:shadow-sm cursor-pointer': onClick,
          }
        )}
      >
        <CardContent className="flex flex-row items-center justify-between px-3 pb-2 pt-4">
          <div>
            <Text variant="cardTitle" className="ml-2 w-full">
              {report.title}
            </Text>
            <Text variant="subtle" className="ml-2 mt-1">
              {formatDate(report.startDate, 'do MMMM yyyy')} -{' '}
              {formatDate(report.endDate, 'do MMMM yyyy')}
            </Text>
          </div>
          {published && !publishing && (
            <div className="flex items-baseline space-x-2">
              <Text className="text-3xl font-medium text-primary">
                {formatBigNumber(report.totalEmissionsTonnes!)}
              </Text>
              <Unit variant="tCO2e" className="text-xl" />
            </div>
          )}
          {publishing && (
            <div className="flex items-center space-x-2">
              <Loader size="xs" />
              <Text className="pr-2 text-xs text-primary-600">
                Publishing...
              </Text>
            </div>
          )}
        </CardContent>
        <CardContent className="flex items-center justify-between space-x-2 px-3 pt-2 ">
          <div className="flex w-full max-w-xl flex-row space-x-4">
            {reportInfoContent.map((info) => (
              <ReportInfo key={info.label} {...info} />
            ))}
          </div>

          <div className="flex space-x-2">
            {report.status === 'published' && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    generatePath(ccfRoutes.COMPANY_REPORT, {
                      report_uuid: report.uuid,
                    })
                  )
                }
              >
                Edit
              </Button>
            )}

            {published && (
              <Link
                aria-disabled={publishing}
                to={
                  !publishing
                    ? generatePath(ccfRoutes.REPORT_RESULT, {
                        report_uuid: report.uuid,
                      })
                    : ''
                }
                className="hover:no-underline"
              >
                <Button className="bg-teal" disabled={publishing}>
                  View report
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
        <CardFooter className="p-0">
          {(report.hasUnreviewedData ||
            ['edited', 'unpublished', 'editing'].includes(report.status!)) && (
            <ReportCardFooter report={report} />
          )}
        </CardFooter>
      </Card>
    </TableCell>
  );
}

interface ReportInfoProps {
  label: string;
  value: string | number;
  alignRight?: boolean;
  unit?: () => JSX.Element;
}
const ReportInfo = ({ label, value, alignRight, unit }: ReportInfoProps) => (
  <div className="ml-2 min-w-20 ">
    <Text variant="subtle">{label}</Text>
    <div className={cn('flex items-end', { 'justify-end': alignRight })}>
      <Text className="mr-1 text-sm">{value}</Text>
      {unit && unit()}
    </div>
  </div>
);

const ReportCardFooter = ({ report }: { report: CompanyReport }) => {
  const navigate = useNavigate();

  const footerState = React.useMemo(() => {
    if (report.status === 'unpublished') {
      return {
        type: 'unpublished' as const,
        bgColor: 'bg-primary-50',
        iconColor: 'text-primary-900/80',
        textColor: 'text-primary-900/80',
        icon: Icons.edit,
        message: 'Report in progress',
        buttonText: 'Continue editing',
      };
    }
    if (report.status === 'edited') {
      return {
        type: 'edited' as const,
        bgColor: 'bg-primary-50',
        iconColor: 'text-primary-900/80',
        textColor: 'text-primary-900/80',
        icon: Icons.edit,
        message: 'Editing in progress',
        buttonText: 'Continue editing',
      };
    }
    return null;
  }, [report.status]);

  if (!footerState) return null;

  const handleAction = () => {
    navigate(
      generatePath(ccfRoutes.COMPANY_REPORT, {
        report_uuid: report.uuid,
      })
    );
  };

  const IconComponent = footerState.icon;

  return (
    <div
      className={cn(
        'size-full rounded-b-lg px-4 z-0 border-t border-primary-100',
        footerState.bgColor
      )}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center space-x-2">
          <IconComponent className={cn('size-4', footerState.iconColor)} />
          <Text className={cn('text-sm font-medium', footerState.textColor)}>
            {footerState.message}
          </Text>
        </div>
        <Button variant="link" onClick={handleAction}>
          {footerState.buttonText}
        </Button>
      </div>
    </div>
  );
};
